/**
 * Pure data-shaping helpers for VMware sFlow charts.
 * No React — call from useMemo in the parent component.
 */
import type { SFlowLog, SFlowSource } from '@/api/vmware/instances';

const PROTO: Record<number, string> = { 1: 'ICMP', 6: 'TCP', 17: 'UDP' };

// ---- Chart slice type (matches @vnetwork/ui PieChart data) ---------------
export interface PieSlice {
  name: string;
  value: number;
}

// ---- Traffic timeline (line, dual-axis: Kbps + packets) ------------------
export interface TimelinePoint {
  /** HH:mm:ss label for x-axis */
  t: string;
  /** Bandwidth in Kbits (bytes × 8 / 1000) */
  kbps: number;
  /** Packet count */
  packets: number;
  [k: string]: unknown;
}

export function buildTimeline(logs: SFlowLog[]): TimelinePoint[] {
  // API returns newest-first; slice first 100, then reverse for chronological display
  return logs.slice(0, 100).reverse().map((log) => {
    const ts = log.timestamp ? new Date(log.timestamp) : new Date(0);
    return {
      t: ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      kbps: Number((((log.bytes ?? 0) * 8) / 1000).toFixed(2)),
      packets: log.packets ?? 0,
    };
  });
}

// ---- Top sources bar chart (by packets, descending) ----------------------
export interface SourceBarPoint {
  /** Source IP label */
  ip: string;
  packets: number;
  [k: string]: unknown;
}

export function buildTopSources(sources: SFlowSource[], limit = 10): SourceBarPoint[] {
  return [...sources]
    .sort((a, b) => (b.total_packets ?? 0) - (a.total_packets ?? 0))
    .slice(0, limit)
    .map((s) => ({ ip: s.src_ip ?? '—', packets: s.total_packets ?? 0 }));
}

// ---- Port distribution pie (top ports by bytes) -------------------------
export function buildPortDistribution(logs: SFlowLog[], topN = 8): PieSlice[] {
  const map = new Map<number, number>();
  for (const log of logs) {
    const port = log.dst_port ?? 0;
    map.set(port, (map.get(port) ?? 0) + (log.bytes ?? 0));
  }
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([port, bytes]) => ({
      name: `Port ${port}`,
      value: Number((bytes / 1048576).toFixed(2)), // MB
    }));
}

// ---- Protocol distribution pie (flow count per protocol) -----------------
export function buildProtocolDistribution(logs: SFlowLog[]): PieSlice[] {
  const map: Record<string, number> = {};
  for (const log of logs) {
    const label = PROTO[log.protocol ?? -1] ?? 'Other';
    map[label] = (map[label] ?? 0) + 1;
  }
  return Object.entries(map)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }));
}

// ---- PPS distribution donut (flow count per pps bucket) -----------------
const PPS_BUCKETS: { label: string; max: number }[] = [
  { label: '0–100',    max: 100 },
  { label: '101–500',  max: 500 },
  { label: '501–1K',   max: 1_000 },
  { label: '1K–5K',    max: 5_000 },
  { label: '5K–10K',   max: 10_000 },
  { label: '10K+',     max: Infinity },
];

export function buildPpsDistribution(logs: SFlowLog[]): PieSlice[] {
  const counts = new Array<number>(PPS_BUCKETS.length).fill(0);
  for (const log of logs) {
    const pps = log.pps ?? 0;
    const idx = PPS_BUCKETS.findIndex((b) => pps <= b.max);
    counts[idx >= 0 ? idx : PPS_BUCKETS.length - 1]++;
  }
  return PPS_BUCKETS
    .map((b, i) => ({ name: b.label, value: counts[i] }))
    .filter((s) => s.value > 0);
}

// ---- Bandwidth distribution donut (flow count per byte-size bucket) -----
const BW_BUCKETS: { label: string; max: number }[] = [
  { label: '0–1 KB',     max: 1_024 },
  { label: '1–10 KB',    max: 10_240 },
  { label: '10–100 KB',  max: 102_400 },
  { label: '100 KB–1 MB', max: 1_048_576 },
  { label: '1–10 MB',    max: 10_485_760 },
  { label: '10 MB+',     max: Infinity },
];

export function buildBandwidthDistribution(logs: SFlowLog[]): PieSlice[] {
  const counts = new Array<number>(BW_BUCKETS.length).fill(0);
  for (const log of logs) {
    const bytes = log.bytes ?? 0;
    const idx = BW_BUCKETS.findIndex((b) => bytes <= b.max);
    counts[idx >= 0 ? idx : BW_BUCKETS.length - 1]++;
  }
  return BW_BUCKETS
    .map((b, i) => ({ name: b.label, value: counts[i] }))
    .filter((s) => s.value > 0);
}
